import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useCreateGroupCriteriaMutation,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetCriteriasQuery,
  useGetGroupsQuery,
  useUpdateGroupCriteriaMutation,
  useUpdateGroupMutation,
} from "reduxFiles/api/assessmentApi";
import { enumToOptions } from "services/helper";
import { Group, GroupStatus, GroupType } from "types/assessmentTypes";
import { Column, TableAction } from "types/globalTypes";

const Groups = () => {
  // hooks
  const { t } = useTranslation();

  // states
  const [item, setItem] = useState<Group | null>(null);
  const confirmModal = useModal();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // apis
  const { data, isFetching, refetch } = useGetGroupsQuery({
    filters: filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreateGroupMutation();
  const [updateApi, { isLoading: isUpdating }] = useUpdateGroupMutation();
  const [deleteApi, { isLoading: isDeleting }] = useDeleteGroupMutation();

  // criterias
  const { data: criterias } = useGetCriteriasQuery();
  const [addCriteriaApi, { isLoading: isAddingCriteria }] =
    useCreateGroupCriteriaMutation();
  const [updateCriteriaApi, { isLoading: isUpdatingCriteria }] =
    useUpdateGroupCriteriaMutation();

  const isLoading =
    isAdding || isUpdating || isAddingCriteria || isUpdatingCriteria;

  // form
  const fields: Field[] = [
    {
      name: "name",
      label: t("group.name"),
      type: "text",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "key",
      label: t("group.key"),
      type: "text",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "description",
      label: t("group.description"),
      type: "textarea",
      formed: true,
    },
    {
      name: "type",
      label: t("group.type"),
      type: "select",
      options: enumToOptions(GroupType, t, "group.types"),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "status",
      label: t("group.status"),
      type: "select",
      options: enumToOptions(GroupStatus, t, "group.statusOptions"),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "criterias",
      label: t("responding.criterias"),
      type: "select",
      formed: true,
      multiple: true,
      options: criterias?.map((opt) => ({
        label: `${opt.index}- ${opt.name}`,
        value: opt.id,
      })),
    },
  ];

  const columns: Column<Group>[] = [
    {
      key: "name",
      title: t("group.name"),
      render: (row) => t(`assessmentGroups.${row.key}`),
    },
    {
      key: "description",
      title: t("group.description"),
      render: (row) => t(`assessmentGroupsDescription.${row.key}`),
    },
    {
      key: "type",
      title: t("group.type"),
      render: (row) => t(`group.types.${row.type}`),
    },
    {
      key: "key",
      title: t("group.key"),
    },
    {
      key: "status",
      title: t("group.status"),
      render: (row) => t(`group.statusOptions.${row.status}`),
    },
    {
      key: "criterias",
      title: t("responding.criterias"),
      render: (row) => row.criterias.length ?? 0,
    },
  ];

  const actions: TableAction<Group>[] = [
    {
      label: t("group.editGroup"),
      onClick: (row) => {
        setItem(row);
        modal.openModal();
      },
      color: "secondary",
      edit: true,
    },
    // {
    //   label: t("group.deleteGroup"),
    //   delete: true,
    //   onClick: (row) => {
    //     setItem(row);
    //     confirmModal.openModal();
    //   },
    //   color: "error",
    // },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
  };

  const handleSubmit = async (formData: FieldValues) => {
    try {
      const { criterias, ...groupData } = formData;

      if (item) {
        await updateApi({
          id: Number(item.id),
          data: groupData,
        }).unwrap();

        if (criterias && Array.isArray(criterias) && criterias.length > 0) {
          const newData = {
            groupId: Number(item.id),
            criteriaIds: criterias,
          };
          await updateCriteriaApi(newData).unwrap();
        }
      } else {
        const createdGroup = await addApi(groupData).unwrap();

        if (criterias && Array.isArray(criterias) && criterias.length > 0) {
          const newData = {
            groupId: Number(createdGroup.id),
            criteriaIds: criterias,
          };
          await addCriteriaApi(newData).unwrap();
        }
      }

      modal.closeModal();
      setItem(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteApi(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("group.groupList")}
          createBtn={{
            title: t("group.addGroup"),
            handler: modal.openModal,
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<Group>
          columns={columns}
          data={data ?? []}
          actions={actions}
          loading={isFetching}
        />

        <DynamicForm
          open={modal.isOpen}
          onClose={() => {
            setItem(null);
            modal.closeModal();
          }}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={
            item
              ? { ...item, criterias: item?.criterias?.map((c) => c.id) }
              : {}
          }
          title={item ? t("group.editGroup") : t("group.addGroup")}
          isEdit={!!item}
          loading={isLoading}
        />

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("group.deleteGroup")}
          desc={t("actions.confirmDeleteDesc", { name: item?.name || "" })}
        />
      </div>
    </div>
  );
};

export default Groups;
